import { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Check, Loader2, ArrowRight } from "lucide-react";
import { API } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLanguage } from "@/context/LanguageContext";

export default function PaymentSuccessPage() {
    const { t } = useLanguage();
    const [searchParams] = useSearchParams();
    const sessionId = searchParams.get("session_id");
    const [status, setStatus] = useState("loading");
    const { clearCart } = useCart();
    const cleared = useRef(false);

    useEffect(() => {
        if (!sessionId) {
            setStatus("error");
            return;
        }
        let attempts = 0;
        const poll = async () => {
            try {
                const res = await axios.get(`${API}/payments/status/${sessionId}`);
                if (res.data.payment_status === "paid") {
                    setStatus("paid");
                    if (!cleared.current) {
                        cleared.current = true;
                        clearCart();
                    }
                    return;
                }
                if (res.data.status === "expired" || res.data.status === "failed") {
                    setStatus("failed");
                    return;
                }
            } catch {
                // keep polling
            }
            attempts += 1;
            if (attempts < 15) {
                setTimeout(poll, 2000);
            } else {
                setStatus("timeout");
            }
        };
        poll();
    }, [sessionId, clearCart]);

    return (
        <main className="pt-16 md:pt-20 min-h-screen flex items-center justify-center px-5" data-testid="payment-success-page">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                className="text-center max-w-md"
            >
                {status === "loading" && (
                    <>
                        <Loader2 size={40} className="mx-auto animate-spin text-zinc-400" />
                        <h1 className="font-display text-3xl font-bold uppercase mt-8" data-testid="payment-status-title">
                            {t("payment.loadingTitle")}
                        </h1>
                        <p className="mt-4 text-sm text-zinc-500">{t("payment.loadingBody")}</p>
                    </>
                )}
                {status === "paid" && (
                    <>
                        <motion.div
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 200, damping: 15 }}
                            className="mx-auto w-16 h-16 bg-black text-white rounded-full flex items-center justify-center"
                        >
                            <Check size={28} />
                        </motion.div>
                        <h1 className="font-display text-3xl md:text-4xl font-bold uppercase mt-8 tracking-tight" data-testid="payment-status-title">
                            {t("payment.paidTitle")}
                        </h1>
                        <p className="mt-4 text-sm text-zinc-500 leading-relaxed">
                            {t("payment.paidBody")}
                        </p>
                        <Link
                            to="/shop"
                            data-testid="payment-success-continue"
                            className="group mt-10 inline-flex items-center gap-3 bg-black text-white px-8 py-4 text-xs tracking-[0.25em] uppercase font-semibold transition-colors duration-300 hover:bg-zinc-800"
                        >
                            {t("payment.continue")}
                            <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
                        </Link>
                    </>
                )}
                {(status === "failed" || status === "timeout" || status === "error") && (
                    <>
                        <h1 className="font-display text-3xl font-bold uppercase" data-testid="payment-status-title">
                            {t("payment.errorTitle")}
                        </h1>
                        <p className="mt-4 text-sm text-zinc-500">
                            {t("payment.errorBody")}
                        </p>
                        <Link
                            to="/"
                            data-testid="payment-error-home"
                            className="mt-10 inline-block border border-black px-8 py-4 text-xs tracking-[0.25em] uppercase font-semibold transition-colors duration-300 hover:bg-black hover:text-white"
                        >
                            {t("payment.backHome")}
                        </Link>
                    </>
                )}
            </motion.div>
        </main>
    );
}
